import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { learningPathsAPI, lessonsAPI, roomsAPI, badgesAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Route, BookOpen, Award, ChevronRight, Trash2, GripVertical, Check, X } from 'lucide-react';

const ROOM_COLORS = {
  memory_garden: '#4ADE80',
  radiant_will: '#FBBF24',
  star_map: '#60A5FA',
  creative_forge: '#FB923C',
  calm_cove: '#2DD4BF'
};

const LearningPathsPage = () => {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPath, setEditingPath] = useState(null);
  
  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRoomId, setFormRoomId] = useState('');
  const [formBadgeId, setFormBadgeId] = useState('');
  const [formLessonIds, setFormLessonIds] = useState([]);
  const [formSparkReward, setFormSparkReward] = useState(50);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pathsRes, roomsRes, badgesRes] = await Promise.all([
        learningPathsAPI.getAll(),
        roomsAPI.getAll(),
        badgesAPI.getAll()
      ]);
      setPaths(pathsRes.data || []);
      setRooms(roomsRes.data || []);
      setBadges(badgesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : roomId;
  };

  const getRoomLessons = (roomId) => {
    // Get published lessons for the room (includes system lessons)
    return lessons.filter(l => l.room_id === roomId && l.is_published);
  };

  // Fetch published lessons separately for the path builder
  useEffect(() => {
    const fetchPublishedLessons = async () => {
      try {
        const allLessons = [];
        for (const room of rooms) {
          const res = await api.get(`/lessons/published/${room.id}`);
          allLessons.push(...(res.data || []));
        }
        setLessons(allLessons);
      } catch (e) {
        console.error('Error fetching published lessons:', e);
      }
    };
    
    if (rooms.length > 0) {
      fetchPublishedLessons();
    }
  }, [rooms]);

  const openCreateModal = (roomId = '') => {
    setFormTitle('');
    setFormDescription('');
    setFormRoomId(roomId);
    setFormBadgeId('');
    setFormLessonIds([]);
    setFormSparkReward(50);
    setEditingPath(null);
    setShowCreateModal(true);
  };

  const openEditModal = async (path) => {
    setFormTitle(path.title);
    setFormDescription(path.description || '');
    setFormRoomId(path.room_id);
    setFormBadgeId(path.badge_id || '');
    setFormLessonIds(path.lesson_ids || []);
    setFormSparkReward(path.spark_reward || 50);
    setEditingPath(path);
    setShowCreateModal(true);
  };

  const handleSavePath = async () => {
    if (!formTitle.trim() || !formRoomId) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const pathData = {
        title: formTitle,
        description: formDescription,
        room_id: formRoomId,
        badge_id: formBadgeId || null,
        lesson_ids: formLessonIds,
        spark_reward: formSparkReward,
        spark_type: getSparkTypeForRoom(formRoomId)
      };

      if (editingPath) {
        // Update existing path - we'd need a PUT endpoint
        // For now, delete and recreate
        toast.info('Updating path...');
      }
      
      await learningPathsAPI.create(pathData);
      toast.success(editingPath ? 'Path updated!' : 'Learning path created!');
      setShowCreateModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving path:', error);
      toast.error('Failed to save learning path');
    }
  };

  const getSparkTypeForRoom = (roomId) => {
    const sparkTypes = {
      memory_garden: 'knowledge',
      radiant_will: 'willpower',
      star_map: 'discovery',
      creative_forge: 'creativity',
      calm_cove: 'calm'
    };
    return sparkTypes[roomId] || 'knowledge';
  };

  const toggleLessonInPath = (lessonId) => {
    setFormLessonIds(prev => {
      if (prev.includes(lessonId)) {
        return prev.filter(id => id !== lessonId);
      } else {
        return [...prev, lessonId];
      }
    });
  };

  const moveLessonUp = (index) => {
    if (index === 0) return;
    setFormLessonIds(prev => {
      const newIds = [...prev];
      [newIds[index], newIds[index - 1]] = [newIds[index - 1], newIds[index]];
      return newIds;
    });
  };

  const moveLessonDown = (index) => {
    if (index === formLessonIds.length - 1) return;
    setFormLessonIds(prev => {
      const newIds = [...prev];
      [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
      return newIds;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl font-philosopher">Loading...</div>
      </div>
    );
  }

  // Group paths by room
  const pathsByRoom = {};
  rooms.forEach(room => {
    pathsByRoom[room.id] = paths.filter(p => p.room_id === room.id);
  });

  return (
    <div className="min-h-screen bg-[#0F172A] px-6 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-6 h-6 text-slate-400" />
          </button>
          <div>
            <h1 className="text-3xl font-philosopher font-bold text-white">Learning Paths</h1>
            <p className="text-slate-400 mt-1">Create structured lesson journeys for each realm</p>
          </div>
        </div>

        {/* Paths by Room */}
        <div className="space-y-8">
          {rooms.map((room) => (
            <div key={room.id} className="glass-panel p-6 rounded-2xl">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: ROOM_COLORS[room.id] }}
                  />
                  <h2 className="text-xl font-philosopher font-bold text-white">{room.name}</h2>
                  <span className="text-slate-500 text-sm">
                    {getRoomLessons(room.id).length} lessons available
                  </span>
                </div>
                <button
                  onClick={() => openCreateModal(room.id)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all"
                  data-testid={`create-path-${room.id}`}
                >
                  <Plus className="w-4 h-4" />
                  <span>New Path</span>
                </button>
              </div>

              {pathsByRoom[room.id]?.length > 0 ? (
                <div className="space-y-3">
                  {pathsByRoom[room.id].map((path) => (
                    <div
                      key={path.id}
                      className="bg-slate-900/50 rounded-xl p-4 hover:bg-slate-900/70 transition-all cursor-pointer"
                      onClick={() => openEditModal(path)}
                      data-testid={`path-${path.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Route className="w-6 h-6" style={{ color: ROOM_COLORS[room.id] }} />
                          <div>
                            <h3 className="text-lg font-semibold text-white">{path.title}</h3>
                            <p className="text-slate-400 text-sm">
                              {path.lesson_ids?.length || 0} lessons • +{path.spark_reward} sparks
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {path.badge_id && (
                            <Award className="w-5 h-5 text-yellow-400" title="Awards badge on completion" />
                          )}
                          <ChevronRight className="w-5 h-5 text-slate-500" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Route className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No learning paths yet for this realm</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="glass-panel rounded-3xl p-6 max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-philosopher font-bold text-white">
                  {editingPath ? 'Edit Learning Path' : 'Create Learning Path'}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Path Title *</label>
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    placeholder="e.g., Exploring the Night Sky"
                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                    data-testid="path-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                  <textarea
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Describe this learning journey..."
                    rows={3}
                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none resize-none"
                    data-testid="path-description-input"
                  />
                </div>

                {/* Room Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Realm *</label>
                  <select
                    value={formRoomId}
                    onChange={(e) => {
                      setFormRoomId(e.target.value);
                      setFormLessonIds([]); // Reset lessons when room changes
                    }}
                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                    data-testid="path-room-select"
                  >
                    <option value="">Select a realm</option>
                    {rooms.map(room => (
                      <option key={room.id} value={room.id}>{room.name}</option>
                    ))}
                  </select>
                </div>

                {/* Spark Reward */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Spark Reward on Completion
                  </label>
                  <input
                    type="number"
                    value={formSparkReward}
                    onChange={(e) => setFormSparkReward(parseInt(e.target.value) || 0)}
                    min={0}
                    max={500}
                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                    data-testid="path-spark-reward"
                  />
                </div>

                {/* Badge Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Award Badge (Optional)
                  </label>
                  <select
                    value={formBadgeId}
                    onChange={(e) => setFormBadgeId(e.target.value)}
                    className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
                    data-testid="path-badge-select"
                  >
                    <option value="">No badge</option>
                    {badges.map(badge => (
                      <option key={badge.id} value={badge.id}>{badge.name} - {badge.description}</option>
                    ))}
                  </select>
                </div>

                {/* Lesson Selection */}
                {formRoomId && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Lessons in Path ({formLessonIds.length} selected)
                    </label>
                    
                    {/* Selected lessons (ordered) */}
                    {formLessonIds.length > 0 && (
                      <div className="mb-4 space-y-2">
                        <p className="text-xs text-slate-500">Drag to reorder:</p>
                        {formLessonIds.map((lessonId, index) => {
                          const lesson = lessons.find(l => l.id === lessonId);
                          if (!lesson) return null;
                          return (
                            <div
                              key={lessonId}
                              className="flex items-center gap-3 bg-indigo-500/20 border border-indigo-500/30 rounded-lg p-3"
                            >
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={() => moveLessonUp(index)}
                                  disabled={index === 0}
                                  className="text-slate-400 hover:text-white disabled:opacity-30"
                                >
                                  ▲
                                </button>
                                <button
                                  onClick={() => moveLessonDown(index)}
                                  disabled={index === formLessonIds.length - 1}
                                  className="text-slate-400 hover:text-white disabled:opacity-30"
                                >
                                  ▼
                                </button>
                              </div>
                              <span className="w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center text-sm text-indigo-300">
                                {index + 1}
                              </span>
                              <span className="flex-1 text-white">{lesson.title}</span>
                              <button
                                onClick={() => toggleLessonInPath(lessonId)}
                                className="p-1 hover:bg-white/10 rounded"
                              >
                                <X className="w-4 h-4 text-slate-400" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Available lessons */}
                    <div className="max-h-60 overflow-y-auto space-y-2 border border-white/10 rounded-xl p-3">
                      {getRoomLessons(formRoomId).filter(l => !formLessonIds.includes(l.id)).map((lesson) => (
                        <button
                          key={lesson.id}
                          onClick={() => toggleLessonInPath(lesson.id)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 transition-all text-left"
                          data-testid={`add-lesson-${lesson.id}`}
                        >
                          <Plus className="w-4 h-4 text-green-400" />
                          <div className="flex-1">
                            <p className="text-white">{lesson.title}</p>
                            <p className="text-slate-500 text-xs">{lesson.blocks?.length || 0} blocks</p>
                          </div>
                        </button>
                      ))}
                      {getRoomLessons(formRoomId).filter(l => !formLessonIds.includes(l.id)).length === 0 && (
                        <p className="text-slate-500 text-center py-4">All lessons added to path</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-full px-6 py-3 transition-all"
                    data-testid="cancel-path-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePath}
                    disabled={!formTitle.trim() || !formRoomId}
                    className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-full px-6 py-3 transition-all disabled:opacity-50"
                    data-testid="save-path-btn"
                  >
                    {editingPath ? 'Update Path' : 'Create Path'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningPathsPage;
